/**
 * Структура рынка: экстремумы, пробои, зоны, ликвидность.
 *
 * То, чем трейдер описывает рынок словами, — «снял ликвидность»,
 * «сломал структуру», «вернулся в имбаланс». В отличие от скользящих
 * средних это не сглаживание ряда, а распознавание событий, и потому
 * здесь свои правила.
 *
 * **Главное: ни одно значение не смотрит вперёд.** Экстремум считается
 * подтверждённым только через `right` баров после него — раньше он не
 * экстремум, а просто высокий бар, за которым может оказаться выше.
 * Поэтому цена свинга появляется в ряду не на самом свинге, а на баре
 * его подтверждения. Это выглядит как запаздывание и им и является:
 * ровно с таким запаздыванием эту структуру видел бы человек в тот
 * момент. Публиковать её задним числом значило бы показать проверку на
 * истории, которой в жизни не бывает, — самая дорогая форма самообмана.
 *
 * Все функции возвращают ряд той же длины, что и бары, с `null` там,
 * где значения ещё нет.
 */
/** Пусто по всей длине: данных не хватило. */
function empty(length) {
    return Array.from({ length }, () => null);
}
/**
 * Свинги: локальные экстремумы, подтверждённые окном справа.
 *
 * Бар считается вершиной, если его максимум выше, чем у `left` баров
 * слева и `right` баров справа. Значение попадает в ряд на баре
 * `pivot + right` — там, где сомнений уже не осталось.
 */
export function swings(bars, left, right) {
    const high = empty(bars.length);
    const low = empty(bars.length);
    const highAt = empty(bars.length);
    const lowAt = empty(bars.length);
    const l = Math.max(1, Math.floor(left));
    const r = Math.max(1, Math.floor(right));
    let lastHigh = null;
    let lastHighAt = null;
    let lastLow = null;
    let lastLowAt = null;
    for (let i = 0; i < bars.length; i += 1) {
        // Проверяем бар, который отстоит на `r` назад: только для него уже
        // известно всё правое окно.
        const pivot = i - r;
        if (pivot >= l) {
            const bar = bars[pivot];
            if (bar) {
                let isHigh = true;
                let isLow = true;
                for (let j = pivot - l; j <= pivot + r; j += 1) {
                    if (j === pivot)
                        continue;
                    const other = bars[j];
                    if (!other)
                        continue;
                    if (other.high >= bar.high)
                        isHigh = false;
                    if (other.low <= bar.low)
                        isLow = false;
                }
                if (isHigh) {
                    lastHigh = bar.high;
                    lastHighAt = pivot;
                }
                if (isLow) {
                    lastLow = bar.low;
                    lastLowAt = pivot;
                }
            }
        }
        high[i] = lastHigh;
        low[i] = lastLow;
        highAt[i] = lastHighAt;
        lowAt[i] = lastLowAt;
    }
    return { high, low, highAt, lowAt };
}
/**
 * Пробой структуры и слом характера.
 *
 * BOS — закрытие за последним подтверждённым экстремумом **в сторону
 * текущего тренда**: рынок продолжает то, что делал. CHoCH — такой же
 * пробой, но против него: первый признак, что делать он собрался
 * другое.
 *
 * Различать их обязательно. Оба выглядят как «цена ушла за уровень», но
 * означают противоположное: один подтверждает движение, другой ставит
 * его под сомнение. Свести их в одно значило бы стереть ровно ту
 * разницу, ради которой трейдер на них и смотрит.
 */
export function structure(bars, left, right) {
    const pivots = swings(bars, left, right);
    const bosUp = empty(bars.length);
    const bosDown = empty(bars.length);
    const chochUp = empty(bars.length);
    const chochDown = empty(bars.length);
    const trend = empty(bars.length);
    /** Куда смотрит структура: 1 вверх, −1 вниз, 0 пока неясно. */
    let direction = 0;
    /** Уровни, пробой которых ещё не засчитан. */
    let openHigh = null;
    let openLow = null;
    /*
     * Уже пробитые уровни.
     *
     * Без этой памяти уровень заряжался бы заново на следующем же баре:
     * последний подтверждённый экстремум никуда не девается, и сравнение
     * с опустевшим `openHigh` снова считало бы его новым. Один пробой
     * превращался бы в непрерывную череду пробоев.
     */
    let usedHigh = null;
    let usedLow = null;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const high = pivots.high[i] ?? null;
        const low = pivots.low[i] ?? null;
        // Новый подтверждённый экстремум снова становится уровнем. Уже
        // пробитый не возвращается: пробой одного уровня дважды — одно
        // событие, а не два.
        if (high !== null && high !== undefined && high !== openHigh && high !== usedHigh) {
            openHigh = high;
        }
        if (low !== null && low !== undefined && low !== openLow && low !== usedLow) {
            openLow = low;
        }
        let up = false;
        let down = false;
        if (openHigh !== null && bar.close > openHigh) {
            up = true;
            // Уровень снят: до появления нового экстремума пробивать нечего.
            usedHigh = openHigh;
            openHigh = null;
        }
        if (openLow !== null && bar.close < openLow) {
            down = true;
            usedLow = openLow;
            openLow = null;
        }
        if (up) {
            // Пробой вверх при нисходящей структуре — слом характера.
            if (direction < 0)
                chochUp[i] = 1;
            else
                bosUp[i] = 1;
            direction = 1;
        }
        if (down) {
            if (direction > 0)
                chochDown[i] = 1;
            else
                bosDown[i] = 1;
            direction = -1;
        }
        if (bosUp[i] === null)
            bosUp[i] = 0;
        if (bosDown[i] === null)
            bosDown[i] = 0;
        if (chochUp[i] === null)
            chochUp[i] = 0;
        if (chochDown[i] === null)
            chochDown[i] = 0;
        trend[i] = direction;
    }
    return { bosUp, bosDown, chochUp, chochDown, trend };
}
/**
 * Снятие ликвидности.
 *
 * Цена заходит за прошлый экстремум тенью и возвращается: стопы за
 * уровнем собраны, а движения не случилось. Отличается от пробоя ровно
 * закрытием — тем же признаком, которым отличается ложный пробой от
 * настоящего, и потому проверяется закрытие, а не касание.
 */
export function sweeps(bars, left, right) {
    const pivots = swings(bars, left, right);
    const high = empty(bars.length);
    const low = empty(bars.length);
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const level = pivots.high[i];
        const floor = pivots.low[i];
        high[i] = level !== null && level !== undefined && bar.high > level && bar.close < level ? 1 : 0;
        low[i] = floor !== null && floor !== undefined && bar.low < floor && bar.close > floor ? 1 : 0;
    }
    return { high, low };
}
/**
 * Разрыв справедливой цены (FVG).
 *
 * Три бара, у которых тени крайних не перекрываются: между ними
 * осталась цена, по которой почти не торговали. Трейдер ждёт, что рынок
 * вернётся её закрыть.
 *
 * Зона живёт, пока цена в неё не зашла, — и перестаёт, как только
 * зашла. Держать её вечно значило бы подсвечивать позапрошлогодний
 * разрыв как повод к сделке.
 *
 * Направление берётся из `side`: вверх — разрыв под ценой (бычий),
 * вниз — над ней.
 */
export function fairValueGaps(bars, side, 
/**
 * Наименьший разрыв, доля от среднего хода бара.
 *
 * Без порога разрывом считается любая щель, включая пятидолларовую на
 * восьмидесятитысячном инструменте. Формально это разрыв; на графике
 * — доля пикселя, а по смыслу — округление, а не место, куда рынок
 * вернётся. Сотня таких зон прячет десяток настоящих.
 */
minSize = 0.25) {
    const upper = empty(bars.length);
    const lower = empty(bars.length);
    const mid = empty(bars.length);
    const active = empty(bars.length);
    const touched = empty(bars.length);
    // Средний ход бара: с чем сравнивать размер разрыва.
    const average = averageRange(bars);
    let zoneUpper = null;
    let zoneLower = null;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        const before = bars[i - 2];
        touched[i] = 0;
        const span = bar && before ? (side === 'up' ? bar.low - before.high : before.low - bar.high) : 0;
        const created = bar !== undefined &&
            before !== undefined &&
            span > 0 &&
            span >= (average[i] ?? 0) * minSize;
        if (created && bar && before) {
            // Новый разрыв затирает прежний: работают с ближайшим.
            zoneUpper = side === 'up' ? bar.low : before.low;
            zoneLower = side === 'up' ? before.high : bar.high;
        }
        else if (bar && zoneUpper !== null && zoneLower !== null) {
            /*
             * Заполнение проверяется только на чужих барах.
             *
             * Бар, создавший разрыв, всегда стоит вплотную к его границе — и
             * без этой оговорки закрывал бы его в момент появления.
             */
            const entered = side === 'up' ? bar.low <= zoneUpper : bar.high >= zoneLower;
            if (entered) {
                touched[i] = 1;
                zoneUpper = null;
                zoneLower = null;
            }
        }
        upper[i] = zoneUpper;
        lower[i] = zoneLower;
        mid[i] = zoneUpper !== null && zoneLower !== null ? (zoneUpper + zoneLower) / 2 : null;
        active[i] = zoneUpper !== null ? 1 : 0;
    }
    return { upper, lower, mid, active, touched };
}
/** Скользящее среднее размаха бара: мера обычного хода инструмента. */
function averageRange(bars) {
    const out = empty(bars.length);
    let value = 0;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const range = bar.high - bar.low;
        value = value === 0 ? range : value * 0.9 + range * 0.1;
        out[i] = value;
    }
    return out;
}
/**
 * Все имбалансы ряда — для графика.
 *
 * Тот же признак и тот же порог размера, что у `fairValueGaps`: условие
 * в конструкторе и зона на графике находят один и тот же разрыв. Разница
 * в том, что здесь помнится каждый, а не последний, и зона считается
 * закрытой, только когда цена прошла её насквозь. Первое касание лишь
 * заполняет её — и насколько, видно на графике.
 */
export function imbalanceZones(bars, minSize = 0.25) {
    const average = averageRange(bars);
    const all = [];
    let open = [];
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        // Сначала заполнение: свеча, создавшая разрыв, его же не заполняет.
        if (open.length > 0) {
            const still = [];
            for (const zone of open) {
                const height = zone.upper - zone.lower;
                const depth = zone.side === 'up' ? zone.upper - bar.low : bar.high - zone.lower;
                if (depth > 0)
                    zone.filled = Math.max(zone.filled, Math.min(1, depth / height));
                if (zone.filled >= 1)
                    zone.closedAt = bar.time;
                else
                    still.push(zone);
            }
            open = still;
        }
        const before = bars[i - 2];
        const middle = bars[i - 1];
        if (!before || !middle)
            continue;
        const floor = (average[i] ?? 0) * minSize;
        const upSpan = bar.low - before.high;
        if (upSpan > 0 && upSpan >= floor) {
            const zone = { side: 'up', start: middle.time, formedAt: bar.time, formedIndex: i, upper: bar.low, lower: before.high, filled: 0, closedAt: null };
            all.push(zone);
            open.push(zone);
        }
        const downSpan = before.low - bar.high;
        if (downSpan > 0 && downSpan >= floor) {
            const zone = { side: 'down', start: middle.time, formedAt: bar.time, formedIndex: i, upper: before.low, lower: bar.high, filled: 0, closedAt: null };
            all.push(zone);
            open.push(zone);
        }
    }
    return all.map(({ side, start, formedAt, upper, lower, filled, closedAt }) => ({
        side,
        start,
        formedAt,
        upper,
        lower,
        filled,
        closedAt,
    }));
}
/**
 * Ордер-блок: последняя свеча против движения перед импульсом.
 *
 * Приближение, и названо им честно: точного определения у ордер-блока
 * нет даже среди тех, кто им торгует. Здесь это последняя противоходная
 * свеча перед баром, который прошёл больше `strength` средних диапазонов
 * — тот отрезок, откуда движение началось.
 */
export function orderBlocks(bars, side, strength) {
    const upper = empty(bars.length);
    const lower = empty(bars.length);
    const mid = empty(bars.length);
    const active = empty(bars.length);
    const touched = empty(bars.length);
    const factor = strength > 0 ? strength : 1.5;
    let zoneUpper = null;
    let zoneLower = null;
    let average = 0;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const range = bar.high - bar.low;
        // Скользящее среднее диапазона: с чем сравнивать импульс.
        average = average === 0 ? range : average * 0.9 + range * 0.1;
        const impulse = range > average * factor;
        const bullish = bar.close > bar.open;
        if (impulse && ((side === 'up' && bullish) || (side === 'down' && !bullish))) {
            // Ищем ближайшую противоходную свечу перед импульсом.
            for (let j = i - 1; j >= Math.max(0, i - 10); j -= 1) {
                const candidate = bars[j];
                if (!candidate)
                    continue;
                const opposite = side === 'up' ? candidate.close < candidate.open : candidate.close > candidate.open;
                if (opposite) {
                    zoneUpper = candidate.high;
                    zoneLower = candidate.low;
                    break;
                }
            }
        }
        touched[i] = 0;
        if (zoneUpper !== null && zoneLower !== null) {
            // Возврат: цена зашла в зону, но ещё не прошла её насквозь.
            const inside = side === 'up' ? bar.low <= zoneUpper : bar.high >= zoneLower;
            if (inside)
                touched[i] = 1;
            // Зона отработана, когда цена прошла её насквозь против стороны.
            const broken = side === 'up' ? bar.close < zoneLower : bar.close > zoneUpper;
            if (broken) {
                zoneUpper = null;
                zoneLower = null;
            }
        }
        upper[i] = zoneUpper;
        lower[i] = zoneLower;
        mid[i] = zoneUpper !== null && zoneLower !== null ? (zoneUpper + zoneLower) / 2 : null;
        active[i] = zoneUpper !== null ? 1 : 0;
    }
    return { upper, lower, mid, active, touched };
}
/**
 * Все ордер-блоки ряда — для графика.
 *
 * Тот же признак, что у `orderBlocks`: последняя противоходная свеча не
 * дальше десяти баров перед импульсом, который прошёл больше `strength`
 * средних диапазонов. Разница — здесь помнится каждый блок, считаются
 * возвращения, а конец зоны — закрытие свечи за ней.
 */
export function orderBlockZones(bars, strength) {
    const factor = strength > 0 ? strength : 1.5;
    const all = [];
    let open = [];
    const used = new Set();
    let average = 0;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        // Сначала старые зоны: импульсная свеча свою же зону не трогает.
        if (open.length > 0) {
            const still = [];
            for (const zone of open) {
                const inside = zone.side === 'up' ? bar.low <= zone.upper : bar.high >= zone.lower;
                if (inside && !zone.inside)
                    zone.touches.push(bar.time);
                zone.inside = inside;
                const broken = zone.side === 'up' ? bar.close < zone.lower : bar.close > zone.upper;
                if (broken)
                    zone.brokenAt = bar.time;
                else
                    still.push(zone);
            }
            open = still;
        }
        const range = bar.high - bar.low;
        average = average === 0 ? range : average * 0.9 + range * 0.1;
        if (range <= average * factor)
            continue;
        const side = bar.close > bar.open ? 'up' : 'down';
        for (let j = i - 1; j >= Math.max(0, i - 10); j -= 1) {
            const candidate = bars[j];
            const opposite = side === 'up' ? candidate.close < candidate.open : candidate.close > candidate.open;
            if (!opposite)
                continue;
            if (used.has(j))
                break;
            used.add(j);
            const zone = {
                side,
                start: candidate.time,
                formedAt: bar.time,
                upper: candidate.high,
                lower: candidate.low,
                touches: [],
                brokenAt: null,
                inside: true,
            };
            all.push(zone);
            open.push(zone);
            break;
        }
    }
    return all.map(({ side, start, formedAt, upper, lower, touches, brokenAt }) => ({
        side,
        start,
        formedAt,
        upper,
        lower,
        touches,
        brokenAt,
    }));
}
/**
 * Положение цены в диапазоне последних экстремумов, 0..100.
 *
 * Ниже пятидесяти — дискаунт, выше — премиум. Названия из той же школы,
 * что FVG и ордер-блоки; смысл простой: дорого сейчас или дёшево
 * относительно того размаха, в котором рынок ходит.
 */
export function premium(bars, left, right) {
    const pivots = swings(bars, left, right);
    const out = empty(bars.length);
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        const high = pivots.high[i];
        const low = pivots.low[i];
        if (!bar || high === null || high === undefined || low === null || low === undefined)
            continue;
        const span = high - low;
        if (span <= 0)
            continue;
        out[i] = Math.max(0, Math.min(100, ((bar.close - low) / span) * 100));
    }
    return out;
}
//# sourceMappingURL=structure.js.map